import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, Mail, Phone as PhoneIcon, MessageCircle, Video } from 'lucide-react';
import './GetQuote.scss';
import { steps } from './Quote';
import Lottie from 'lottie-react';
import tickAnimation from '../../Tick Animation - KoolMind.json';
import StepContent from './StepContent';

const GetQuote = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({});
  const [otherValues, setOtherValues] = useState({});
  const [attachments, setAttachments] = useState([]);
  const fileInputRef = useRef(null);

  const [otpEmail, setOtpEmail] = useState(formData.otpEmail || '');
  const [showOtpSuccess, setShowOtpSuccess] = useState(false);

  const isValidEmail = (email) => {
    if (!email) return false;
    const atIndex = email.indexOf('@');
    if (atIndex < 1) return false;
    const dotIndex = email.lastIndexOf('.');
    if (dotIndex < atIndex + 2) return false;
    if (dotIndex === email.length - 1) return false;
    return true;
  };

  const [otpCode, setOtpCode] = useState(Array(6).fill(''));
  const [otpResendTimer, setOtpResendTimer] = useState(60);
  const otpInputs = Array(6).fill(0);
  const otpInputRefs = useMemo(() => Array.from({ length: 6 }, () => React.createRef()), []);
  
  useEffect(() => {
    let timer;
    if (currentStep === 13 && otpResendTimer > 0) {
      timer = setTimeout(() => setOtpResendTimer(otpResendTimer - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [otpResendTimer, currentStep]);

  useEffect(() => {
    if (currentStep === 12 && formData.email && !otpEmail) {
      setOtpEmail(formData.email);
      setFormData({ ...formData, otpEmail: formData.email });

      if (isValidEmail(formData.email)) {
        setShowOtpSuccess(true);
      }
    }
  }, [currentStep, formData.email, otpEmail]);

  const handleOtpChange = (idx, val) => {
    if (!/^[0-9]?$/.test(val)) return;
    const updated = [...otpCode];
    updated[idx] = val;
    setOtpCode(updated);
    setFormData({ ...formData, otpCode: updated.join('') });
    if (val && idx < 5) {
      otpInputRefs[idx + 1].current && otpInputRefs[idx + 1].current.focus();
    }
    if (!val && idx > 0) {
      otpInputRefs[idx - 1].current && otpInputRefs[idx - 1].current.focus();
    }
  };
  const handleOtpPaste = (e) => {
    const val = e.clipboardData.getData('Text').replace(/\D/g, '').slice(0, 6);
    if (val.length === 6) {
      setOtpCode(val.split(''));
      setFormData({ ...formData, otpCode: val });
      otpInputRefs[5].current && otpInputRefs[5].current.focus();
    }
  };

  const [showThankYou, setShowThankYou] = useState(false);

  const step = steps[currentStep - 1];
  const totalSteps = steps.length;
  const skipSteps = [3, 4, 6, 8]; 

  const handleSelect = (id) => {
    if (step.type === 'single') {
      setFormData({ ...formData, [step.key]: id });
    } else if (step.type === 'multi') {
      const prev = formData[step.key] || [];
      setFormData({
        ...formData,
        [step.key]: prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id],
      });
    } else if (step.type === 'boolean') {
      setFormData({ ...formData, [step.key]: id });
    }
  };

  const handleOtherChange = (value) => {
    setOtherValues({ ...otherValues, [step.key]: value });
  };

  const isNextDisabled = () => {
    if (currentStep === 13 || step.type === 'otp_code') {
      return otpCode.join('').length !== 6;
    }
    if (currentStep === 12 || step.type === 'otp') {
      return !isValidEmail(otpEmail);
    }
    if (skipSteps.includes(currentStep - 1)) {
      // For skip steps, Next is always enabled
      return false;
    }
    if (step.type === 'single') return !formData[step.key];
    if (step.type === 'multi') {
      if (step.allowOther) {
        return !(formData[step.key] && formData[step.key].length > 0) && !otherValues[step.key];
      }
      return !(formData[step.key] && formData[step.key].length > 0);
    }
    if (step.type === 'boolean' && step.required) return formData[step.key] === undefined;
    return false;
  };

  const communicationOptions = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'phone', label: 'Phone', icon: PhoneIcon },
    { id: 'whatsapp', label: 'Whatsapp', icon: MessageCircle },
    { id: 'zoom', label: 'Zoom/Meet', icon: Video },
  ];

  const downloadJSON = (data, filename = 'quote-data.json') => {
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleNext = async () => {
    if (currentStep < totalSteps && !isNextDisabled()) {
      setCurrentStep(currentStep + 1);
    } else if (currentStep === totalSteps && !isNextDisabled()) {
      // 1. Build the message as HTML
      let message = `<b>Quote Request Submission</b><br/><br/>`;
      if (formData.fullName) message += `<b>Name:</b> ${formData.fullName}<br/>`;
      if (formData.email) message += `<b>Email:</b> ${formData.email}<br/><br/>`;

      let qNum = 1;
      steps.forEach((step) => {
        if (formData[step.key] !== undefined && step.label) {
          let answer = formData[step.key];
          
          // Handle different answer types
          if (Array.isArray(answer)) {
            // For multi-select questions, check if "other" is selected
            if (answer.includes('other') && otherValues[step.key]) {
              // Replace "other" with the actual written text
              answer = answer.map(item => item === 'other' ? otherValues[step.key] : item).join(', ');
            } else {
              answer = answer.join(', ');
            }
          } else if (typeof answer === 'boolean') {
            answer = answer ? 'Yes' : 'No';
          } else if (answer === 'other' && otherValues[step.key]) {
            // For single-select "other" questions, use the written text
            answer = otherValues[step.key];
          }
          
          message += `<b>Q${qNum}. ${step.label}</b><br/>${answer}<br/><br/>`;
          qNum++;
        }
      });

      // Attachments (step 10, usually key is 'attachments')
      if (attachments && attachments.length > 0) {
        message += `<b>Attachments:</b><br/>`;
        attachments.forEach((file, idx) => {
          message += `${idx + 1}. ${file.name} (${(file.size / 1024 / 1024).toFixed(1)} MB)<br/>`;
        });
        message += `<br/>`;
      }

      // 2. Prepare the payload
      const payload = {
        email: formData.email,
        name: formData.fullName,
        message,
      };

      console.log(payload, 'payload');

      // // 3. Send to backend
      // const url = `${process.env.REACT_APP_BASE_URL}/contact`;
      // await fetch(url, {
      //   method: "POST",
      //   headers: { "Content-Type": "application/json" },
      //   body: JSON.stringify(payload),
      // });

      // setShowThankYou(true);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const handleSkip = () => {
    setFormData({ ...formData, [step.key]: undefined });
    setOtherValues({ ...otherValues, [step.key]: undefined });
    setCurrentStep(currentStep + 1);
  };

  const getProgressWidth = () => `${(currentStep / totalSteps) * 100}%`;

  const getFileIcon = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    if (file.type.startsWith('image/')) return <span role="img" aria-label="image">🖼️</span>;
    if (ext === 'pdf') return <span role="img" aria-label="pdf">📄</span>;
    if (["doc", "docx"].includes(ext)) return <span role="img" aria-label="doc">📝</span>;
    if (["xls", "xlsx"].includes(ext)) return <span role="img" aria-label="excel">📊</span>;
    if (["txt"].includes(ext)) return <span role="img" aria-label="txt">📃</span>;
    return <span role="img" aria-label="file">📁</span>;
  };

  const handleFilePreview = (file) => {
    const ext = file.name.split('.').pop().toLowerCase();
    const url = URL.createObjectURL(file);
    if (file.type.startsWith('image/')) {
      window.open(url, '_blank');
    } else if (ext === 'pdf') {
      window.open(url, '_blank');
    } else {
      window.open(url, '_blank'); 
    }
  };

  if (showThankYou) {
    return (
      <div className="thank-you-section">
        <div className="thank-you-animation">
          <Lottie animationData={tickAnimation} loop={false} style={{ width: 180, height: 180, margin: '0 auto' }} />
        </div>
        <h1 className="thank-you-title">Thank You! Your Request Has Been Received</h1>
        <p className="thank-you-desc">We've received your project details and are preparing a personalized quote. You'll receive a PDF quotation at your verified email within 10–15 minutes.</p>
        <p className="thank-you-note">Make sure to check your inbox (and spam folder) for an email from Koolmind.</p>
        <button className="thank-you-btn" onClick={() => { setShowThankYou(false); setCurrentStep(1); setFormData({}); setOtherValues({}); setAttachments([]); setOtpEmail(''); setOtpCode(Array(6).fill('')); }}>
          Start Another Project
        </button>
      </div>
    );
  }

  return (
    <div className="get-quote-section ">
      <div className="get-quote-container container">
        <div className="quote-header">
          <h2>Get a quote for your dream app</h2>
          <p>Tell us about your idea, and we'll guide you through the process.</p>
        </div>
        <div className="quote-progress">
          <div className="step-indicator">
            <div className="step active">
              <span className="step-number">{currentStep}</span>
              <span className="step-text">Step {currentStep} of {totalSteps}</span>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: getProgressWidth() }}></div>
            </div>
          </div>
        </div>
        <StepContent
          currentStep={currentStep}
          step={step}
          formData={formData}
          setFormData={setFormData}
          handleSelect={handleSelect}
          otherValues={otherValues}
          handleOtherChange={handleOtherChange}
          fileInputRef={fileInputRef}
          attachments={attachments}
          setAttachments={setAttachments}
          handleFilePreview={handleFilePreview}
          getFileIcon={getFileIcon}
          communicationOptions={communicationOptions}
          otpEmail={otpEmail}
          setOtpEmail={setOtpEmail}
          setShowOtpSuccess={setShowOtpSuccess}
          isValidEmail={isValidEmail}
          otpInputs={otpInputs}
          otpCode={otpCode}
          otpInputRefs={otpInputRefs}
          handleOtpChange={handleOtpChange}
          handleOtpPaste={handleOtpPaste}
          otpResendTimer={otpResendTimer}
          setOtpResendTimer={setOtpResendTimer}
        />
        <div className="quote-navigation">
          <button
            className="nav-button btn-back"
            onClick={handleBack}
            disabled={currentStep === 1}
          >
            Back
          </button>
          <div className="nav-button-container">
            {skipSteps.includes(currentStep - 1) && (
              <button
                className="nav-button btn-next"
                style={{ background: 'none', color: '#1886cd', border: 'none', marginRight: 8 }}
                onClick={handleSkip}
              >
                Skip
              </button>
            )}
            <button
              className="nav-button btn-next"
              onClick={handleNext}
              disabled={isNextDisabled()}
            >
              {currentStep === 13 ? 'Submit' : 'Next'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GetQuote;